import { KeyboardBindType } from "@1games-ru/region-shared/dist/enums/account/settings";
import { isEmpty } from "lodash";
import { useCallback, useEffect } from "react";
import useAppSelector from "@/core/helpers/useAppSelector";
import { useKeyPress } from "@/core/keyPressManager";
import {
  ExtendedNotificationType,
  NotificationItemOffer,
  useNotificationStore,
} from "@/stores/notifications/notificationStore";
import useOfferHandlers from "@/domains/notifications/hooks/useOfferHandlers.ts";
import { controlSettingsSelector } from "@/domains/settings/control/slice";
import KeyCode from "@/assets/constants/key-code";

export default function useKeyboardEvent() {
  const controlSettings = useAppSelector(controlSettingsSelector);
  const { subscribeKeyUp, unsubscribeKeyUp } = useKeyPress();
  const { acceptHandler, declineHandler } = useOfferHandlers();

  const { stack } = useNotificationStore();

  const accept = useCallback(() => {
    const firstOfferNotificationItem = stack.find(
      (item) => item.type === ExtendedNotificationType.Offer,
    );

    if (!firstOfferNotificationItem) return;

    acceptHandler(
      firstOfferNotificationItem.id,
      firstOfferNotificationItem as NotificationItemOffer,
    );
  }, [acceptHandler, stack]);

  const decline = useCallback(() => {
    const firstOfferNotificationItem = stack.find(
      (item) => item.type === ExtendedNotificationType.Offer,
    );

    if (!firstOfferNotificationItem) return;

    declineHandler(firstOfferNotificationItem.id);
  }, [declineHandler, stack]);

  const handleKeyUp = useCallback(
    async (keyCode: KeyCode) => {
      if (isEmpty(controlSettings)) return;

      switch (keyCode) {
        case controlSettings[KeyboardBindType.ACCEPT].key: {
          accept();
          break;
        }
        case controlSettings[KeyboardBindType.DECLINE].key: {
          decline();
          break;
        }
        default:
          break;
      }
    },
    [accept, controlSettings, decline],
  );

  useEffect(() => {
    subscribeKeyUp("notifications", handleKeyUp);

    return () => {
      unsubscribeKeyUp("notifications");
    };
  }, [subscribeKeyUp, handleKeyUp, unsubscribeKeyUp]);
}
